import React, { useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogIn } from 'lucide-react'
import Button from '../components/ui/Button'
import Layout from '../components/ui/Layout'
import { supabase } from '../../lib/supabase'
import { saveToLocal, shakeElement, cn } from '../lib/utils'
import { ROUTE_SIGNATURE } from '../lib/routeTheme'

const STORAGE_KEY = 'currentTeam'
const CAPTAIN_DEMO_NAME = 'captain test'
const CAPTAIN_DEMO_PIN = '1234'

/** Maps Supabase + validation outcomes to user-facing copy */
const ERR = {
  validationBand: 'Enter your Team Name.',
  validationPasscode: 'Team PIN must be 4 digits.',
  wrongPasscode: "That team name and PIN don't match. Double-check spelling and punctuation.",
  network: 'Connection problem. Check signal and try again.',
  unknown: 'Something went wrong. Try again.',
}

function normalizeBandName(value) {
  return String(value || '').trim()
}

function isCaptainDemoLogin(name, code) {
  return name.toLowerCase() === CAPTAIN_DEMO_NAME && code === CAPTAIN_DEMO_PIN
}

export default function Login() {
  const navigate = useNavigate()
  const [bandName, setBandName] = useState('')
  const [passcode, setPasscode] = useState('')
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const formRef = useRef(null)

  const clearError = useCallback(() => setError(null), [])

  const onBandChange = (e) => {
    clearError()
    setBandName(e.target.value)
  }

  const onPasscodeChange = (e) => {
    clearError()
    setPasscode(e.target.value.replace(/\D/g, '').slice(0, 4))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const name = normalizeBandName(bandName)
    const code = passcode.trim()

    if (!name) {
      shakeElement(formRef)
      setError(ERR.validationBand)
      return
    }
    if (code.length !== 4 || !/^\d{4}$/.test(code)) {
      shakeElement(formRef)
      setError(ERR.validationPasscode)
      return
    }

    setError(null)
    setIsLoading(true)

    try {
      const usingCaptainDemo = isCaptainDemoLogin(name, code)
      const rpc = usingCaptainDemo
        ? supabase.rpc('start_demo_session')
        : supabase.rpc('get_team_for_login', {
            p_name: name,
            p_pin: code,
          })
      const { data: loginJson, error: rpcError } = await rpc

      if (rpcError) {
        console.error('Supabase login RPC:', rpcError)
        shakeElement(formRef)
        setError(ERR.network)
        setIsLoading(false)
        return
      }

      if (!loginJson?.found || !loginJson.team?.id) {
        shakeElement(formRef)
        setError(ERR.wrongPasscode)
        setIsLoading(false)
        return
      }

      // Includes starting_hole_id when set (shotgun); persisted for offline route fallback.
      const team = {
        ...loginJson.team,
        isDemo: Boolean(loginJson?.team?.is_demo),
      }

      saveToLocal(STORAGE_KEY, team)
      saveToLocal(`completed_holes_${team.id}`, [])
      saveToLocal(`team_score_${team.id}`, 0)
      navigate('/dashboard')
    } catch (err) {
      console.error('Login error:', err)
      shakeElement(formRef)
      setError(ERR.unknown)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Layout>
      <div
        role="main"
        className="login-page relative z-10 flex min-h-[100dvh] items-center justify-center p-4 py-8"
      >
        <div
          ref={formRef}
          className={cn(
            'login-page__panel texture-paper relative z-10 w-full max-w-md mx-auto rounded-2xl border-4 shadow-xl p-6 sm:p-8 text-ink pointer-events-auto',
            ROUTE_SIGNATURE.login.panel
          )}
        >
          <header className="login-page__header mb-8 text-center">
            <h1 className="login-page__title mb-3">
              <img
                src="/brand/puttzin-logo.svg"
                alt="Puttzin' Around"
                className="login-page__brand mx-auto h-24 sm:h-28 w-auto max-w-[min(100%,320px)] object-contain brightness-0 opacity-[0.92]"
                width={320}
                height={128}
                decoding="async"
              />
            </h1>
            <p className="login-page__subtitle font-setlist text-sm text-ink/80 mt-1">
              Downtown mini golf crawl · 12 holes
            </p>
          </header>

          <form className="login-form space-y-6" onSubmit={handleSubmit} noValidate>
            <div className="login-form__field">
              <label className="login-form__label block text-sm font-setlist font-bold uppercase tracking-wide text-ink/90 mb-1" htmlFor="login-band-name">
                Team Name
              </label>
              <input
                id="login-band-name"
                name="teamName"
                type="text"
                autoComplete="organization"
                autoCapitalize="words"
                value={bandName}
                onChange={onBandChange}
                className="login-form__input w-full min-h-[44px] px-3 py-2 rounded border border-ink/25 bg-white/55 text-ink placeholder:text-ink/45 focus:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/40"
                placeholder="Team Name"
                aria-invalid={error === ERR.validationBand || error === ERR.wrongPasscode}
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            <div className="login-form__field">
              <label className="login-form__label block text-sm font-setlist font-bold uppercase tracking-wide text-ink/90 mb-1" htmlFor="login-vip-passcode">
                Team PIN (4 digits)
              </label>
              <input
                id="login-vip-passcode"
                name="teamPin"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                enterKeyHint="done"
                maxLength={4}
                value={passcode}
                onChange={onPasscodeChange}
                className="login-form__input login-form__input--passcode w-full min-h-[44px] px-3 py-2 rounded border border-ink/25 bg-white/55 text-center tracking-[0.35em] text-lg text-ink placeholder:text-ink/40 focus:border-neon-cyan focus:outline-none focus:ring-2 focus:ring-neon-cyan/40"
                placeholder="••••"
                aria-invalid={
                  error === ERR.wrongPasscode || error === ERR.validationPasscode
                }
                aria-describedby={error ? 'login-error' : undefined}
              />
            </div>

            {error && (
              <p
                id="login-error"
                role="alert"
                className="login-form__error text-sm rounded px-3 py-2 border border-red-500/50 bg-red-500/10"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              variant="sticker"
              disabled={isLoading}
              className="login-form__submit w-full min-h-[48px]"
            >
              <span className="inline-flex items-center justify-center gap-2">
                <LogIn className="w-5 h-5 shrink-0" aria-hidden />
                {isLoading ? 'Signing in…' : 'Start playing'}
              </span>
            </Button>
          </form>

          <p className="login-page__help mt-8 text-center text-sm text-ink/70">
            Your team captain got the PIN via email. Ask any volunteer on-site if you need help.
          </p>
        </div>
      </div>
    </Layout>
  )
}
